import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type LinkRow = {
  id: number
  niq_key: string
  resolved_by: string
  fanout_count: number
  leaf: {
    id: number
    canonical_name: string
    pack_size: string | null
    star_product_code: string | null
    brand: string
    manufacturer: string
  }
}

type Props = {
  links: LinkRow[]
  pagination: { total_count: number; page: number; per_page: number }
  filters: { resolved_by: string | null; fanout: string | null; search: string | null }
  counts: { total: number; auto: number; human: number; one_to_many: number }
}

const PATH = "/admin/product-market-links"
const DESCRIPTION =
  "Audit tautan jembatan own↔NIQ (bridge pasar). Tinjau tautan otomatis dan fan-out 1-ke-banyak; konfirmasi yang benar, lepas yang salah."

function pushQuery(next: Record<string, string | null | undefined>) {
  const q: Record<string, string> = {}
  Object.entries(next).forEach(([k, v]) => {
    if (v != null && v !== "") q[k] = String(v)
  })
  router.get(PATH, q, { preserveState: true, preserveScroll: true, replace: true })
}

export default function Index({ links, pagination, filters, counts }: Props) {
  const { props } = usePage<PageProps>()
  const dq = props.product_master_dq_count ?? 0
  const [search, setSearch] = React.useState(filters.search ?? "")
  const totalPages = Math.max(1, Math.ceil(pagination.total_count / pagination.per_page))

  const baseQuery = () => ({
    resolved_by: filters.resolved_by,
    fanout: filters.fanout,
    search: filters.search,
  })

  const confirmLink = (id: number) =>
    router.patch(`${PATH}/${id}`, {}, { preserveScroll: true })

  const unlink = (link: LinkRow) => {
    if (!confirm(`Lepas tautan ${link.niq_key} dari ${link.leaf.canonical_name}?`)) return
    router.delete(`${PATH}/${link.id}`, { preserveScroll: true })
  }

  return (
    <>
      <Head title="Tautan Pasar — Produk Master">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Tautan Pasar — Produk Master" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/product-master" className="hover:text-ink-body">
            Produk Master
          </Link>
          <span>/</span>
          <span className="font-medium text-ink-body">Tautan Pasar</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Tinjau Tautan Pasar</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
          <div className="mt-4 inline-flex rounded-lg border border-hairline p-1">
            <Link
              href="/admin/product-aliases"
              className="rounded-md px-3 py-1.5 text-sm text-ink-muted hover:text-ink-body"
            >
              Antrian DQ{dq > 0 ? ` (${dq})` : ""}
            </Link>
            <span className="rounded-md bg-surface px-3 py-1.5 text-sm font-medium text-ink-body">
              Tautan Pasar ({counts.total})
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>{counts.auto} otomatis</span>
            <span>{counts.human} terkonfirmasi</span>
            <span>{counts.one_to_many} node 1-ke-banyak</span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              pushQuery({ ...baseQuery(), search })
            }}
            className="flex-1 min-w-[220px]"
          >
            <label htmlFor="search">Cari</label>
            <div className="relative mt-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                aria-hidden="true"
              />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="NIQ key, nama produk, atau kode STAR"
                className="pl-9"
              />
            </div>
          </form>
          <div className="min-w-[170px]">
            <label htmlFor="resolved_by">Resolusi</label>
            <Select
              id="resolved_by"
              className="mt-1"
              value={filters.resolved_by ?? ""}
              onChange={(e) => pushQuery({ ...baseQuery(), resolved_by: e.target.value })}
            >
              <option value="">Semua</option>
              <option value="auto">Otomatis</option>
              <option value="human">Terkonfirmasi</option>
            </Select>
          </div>
          <label className="flex min-h-[2.5rem] items-center gap-2 font-normal text-ink-body">
            <input
              type="checkbox"
              checked={filters.fanout === "1"}
              onChange={(e) => pushQuery({ ...baseQuery(), fanout: e.target.checked ? "1" : null })}
            />
            Hanya 1-ke-banyak
          </label>
        </div>

        {/* Links */}
        {links.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Tidak ada tautan yang cocok dengan filter.</p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">NIQ key</th>
                  <th className="px-4 py-3 font-medium">Leaf own</th>
                  <th className="px-4 py-3 font-medium">Fan-out</th>
                  <th className="px-4 py-3 font-medium">Resolusi</th>
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.id} className="border-t border-hairline">
                    <td className="px-4 py-3 font-mono text-xs text-ink-body">{l.niq_key}</td>
                    <td className="px-4 py-3">
                      <Link href={`/admin/product-master/${l.leaf.id}`} className="text-ink-body">
                        {l.leaf.canonical_name}
                      </Link>
                      <div className="text-xs text-ink-muted">
                        {l.leaf.brand}
                        {l.leaf.pack_size ? ` · ${l.leaf.pack_size}` : ""}
                        {l.leaf.star_product_code ? ` · ${l.leaf.star_product_code}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {l.fanout_count > 1 ? (
                        <Badge tone="signal">1-ke-{l.fanout_count}</Badge>
                      ) : (
                        <span className="text-ink-muted">1-ke-1</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {l.resolved_by === "human" ? (
                        <Badge tone="signal">Manual</Badge>
                      ) : (
                        <Badge tone="muted">Otomatis</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {l.resolved_by !== "human" && (
                          <Button size="sm" variant="secondary" onClick={() => confirmLink(l.id)}>
                            Konfirmasi
                          </Button>
                        )}
                        <Button size="sm" variant="danger" onClick={() => unlink(l)}>
                          Lepas
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
            <span>
              Halaman {pagination.page} dari {totalPages} ({pagination.total_count})
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => pushQuery({ ...baseQuery(), page: String(pagination.page - 1) })}
              >
                Sebelumnya
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => pushQuery({ ...baseQuery(), page: String(pagination.page + 1) })}
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </AppShell>
    </>
  )
}
